import * as Tone from 'tone';
import type { TriggerOpts, Voice } from './audio';

export type Track = {
  name: string;
  steps: boolean[];
  pitches?: number[]; // MIDI note per step; only set for pitched tracks
  pitchOffset?: number; // semitones added to every pitch (tone-row octave shift)
  voice: Voice;
  accent?: number; // multiplier on velocity
  muted?: boolean; // local-only monitoring mute — skipped during playback, never persisted
  audible?: boolean; // derived source availability — false for hidden 808-only rows outside 808
};

export const DEFAULT_STEPS = 16;
export const MAX_STEPS = 64;

export class Sequencer {
  tracks: Track[];
  numSteps: number = DEFAULT_STEPS;
  currentStep = 0;
  private seq: Tone.Sequence | null = null;
  private onTick: (step: number) => void;

  constructor(tracks: Track[], onTick: (step: number) => void, numSteps = DEFAULT_STEPS) {
    this.tracks = tracks;
    this.numSteps = numSteps;
    this.onTick = onTick;
  }

  toggle(trackIdx: number, stepIdx: number): boolean {
    const v = !this.tracks[trackIdx].steps[stepIdx];
    this.tracks[trackIdx].steps[stepIdx] = v;
    return v;
  }

  clear(): void {
    for (const t of this.tracks) t.steps.fill(false);
  }

  // Resize the pattern. Growing duplicates the existing pattern (so bar 2 starts
  // as a copy of bar 1, etc.). Shrinking truncates. Restarts the underlying
  // Tone.Sequence if currently playing.
  resize(newNumSteps: number): void {
    if (newNumSteps === this.numSteps || newNumSteps < DEFAULT_STEPS || newNumSteps > MAX_STEPS) return;
    const wasPlaying = this.isPlaying();
    if (wasPlaying) this.stop();

    if (newNumSteps > this.numSteps) {
      while (this.tracks[0].steps.length < newNumSteps) {
        for (const t of this.tracks) {
          t.steps = [...t.steps, ...t.steps];
          if (t.pitches) t.pitches = [...t.pitches, ...t.pitches];
        }
      }
      // trim any overshoot
      for (const t of this.tracks) {
        t.steps = t.steps.slice(0, newNumSteps);
        if (t.pitches) t.pitches = t.pitches.slice(0, newNumSteps);
      }
    } else {
      for (const t of this.tracks) {
        t.steps = t.steps.slice(0, newNumSteps);
        if (t.pitches) t.pitches = t.pitches.slice(0, newNumSteps);
      }
    }
    this.numSteps = newNumSteps;

    if (wasPlaying) this.start();
  }

  start(): void {
    if (this.seq) return;
    const indices = Array.from({ length: this.numSteps }, (_, i) => i);
    this.seq = new Tone.Sequence(
      (time, step) => {
        const stepDur = Tone.Time('16n').toSeconds();
        for (const t of this.tracks) {
          if (t.muted || t.audible === false) continue;
          if (!t.steps[step]) continue;
          const accent = step % 4 === 0 ? 1.0 : 0.85;
          const pitch = t.pitches ? t.pitches[step] + (t.pitchOffset ?? 0) : undefined;
          // For pitched tracks: tell the voice whether the prior step was on
          // (so it can glide instead of re-attacking) and when to release if
          // this onset ends a run of consecutive on-steps.
          let opts: TriggerOpts | undefined;
          if (t.pitches) {
            const n = t.steps.length;
            const prev = (step - 1 + n) % n;
            const next = (step + 1) % n;
            opts = {
              legatoFromPrev: t.steps[prev],
              releaseAt: t.steps[next] ? undefined : time + stepDur - 0.001,
            };
          }
          t.voice.trigger(time, accent * (t.accent ?? 1), pitch, opts);
        }
        Tone.getDraw().schedule(() => {
          this.currentStep = step;
          this.onTick(step);
        }, time);
      },
      indices,
      '16n'
    );
    this.seq.start(0);
    Tone.getTransport().start('+0', 0);
  }

  stop(): void {
    if (this.seq) {
      this.seq.stop();
      this.seq.dispose();
      this.seq = null;
    }
    Tone.getTransport().stop();
    this.currentStep = -1;
    this.onTick(-1);
  }

  setBpm(bpm: number): void {
    Tone.getTransport().bpm.value = bpm;
  }

  isPlaying(): boolean {
    return Tone.getTransport().state === 'started';
  }
}
